import { useCallback, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";

import { LoadingPage } from "@/components/shared/LoadingPage";
import { PageHeader } from "@/components/shared/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDisplayDate, parseLocalDateOnly } from "@/lib/dateFormat";
import {
  canResendDailyReport,
  dailyReportDisplayStatus,
  getDailyReport,
  resendDailyReport,
  type DailyReportStatus,
} from "@/services/dailyReportsService";
import { ChevronDown, Copy, Loader2, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";

function statusBadgeVariant(
  displayStatus: string
): "default" | "secondary" | "destructive" | "outline" {
  switch (displayStatus) {
    case "ack_ok":
      return "default";
    case "needs_attention":
      return "destructive";
    case "retrying":
      return "outline";
    case "pending":
    case "sent":
      return "secondary";
    default:
      return "outline";
  }
}

function statusBadgeClassName(displayStatus: string): string | undefined {
  if (displayStatus === "ack_ok") {
    return "border-green-500/20 bg-green-500/10 text-green-700 dark:text-green-400";
  }
  if (displayStatus === "retrying") {
    return "border-amber-500/20 bg-amber-500/10 text-amber-700 dark:text-amber-400";
  }
  return undefined;
}

export function DailyReportDetailPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["daily-reports", reportId],
    queryFn: () => getDailyReport(reportId!),
    enabled: Boolean(reportId),
  });

  useEffect(() => {
    if (isError) {
      toast.error(t("dailyReports.detail.loadError"));
    }
  }, [isError, t]);

  const handleCopyAckXml = useCallback(async () => {
    const xml = data?.ackXml;
    if (!xml) return;
    try {
      await navigator.clipboard.writeText(xml);
      toast.success(t("dailyReports.detail.copyAckSuccess"));
    } catch {
      toast.error(t("dailyReports.detail.copyAckError"));
    }
  }, [data?.ackXml, t]);

  const handleCopySignedXml = useCallback(async () => {
    const xml = data?.signedXml;
    if (!xml) return;
    try {
      await navigator.clipboard.writeText(xml);
      toast.success(t("dailyReports.detail.copySentReportSuccess"));
    } catch {
      toast.error(t("dailyReports.detail.copySentReportError"));
    }
  }, [data?.signedXml, t]);

  const resendMutation = useMutation({
    mutationFn: () => {
      if (!reportId) {
        return Promise.reject(new Error("missing report id"));
      }
      return resendDailyReport(reportId);
    },
    onMutate: () => {
      if (reportId) {
        toast.loading(t("dailyReports.list.resendLoading"), {
          id: `daily-report-resend-${reportId}`,
        });
      }
    },
    onSuccess: async (result) => {
      /** Server already persisted final DGI outcome; apply immediately so UI matches the toast (invalidate refetch can lag). */
      queryClient.setQueryData(["daily-reports", result.id], result);
      if (reportId && result.id !== reportId) {
        queryClient.removeQueries({ queryKey: ["daily-reports", reportId] });
        navigate(`/dgi/daily-reports/${result.id}`, { replace: true });
      }
      await queryClient.invalidateQueries({ queryKey: ["daily-reports"] });
      const toastId = `daily-report-resend-${reportId ?? result.id}`;
      if (result.status === "ack_ok") {
        toast.success(t("dailyReports.list.resendSuccess"), { id: toastId });
      } else if (result.status === "ack_rejected") {
        toast.error(t("dailyReports.list.resendRejectedByDgi"), { id: toastId });
      } else {
        toast.error(t("dailyReports.list.resendSendFailed"), { id: toastId });
      }
    },
    onError: () => {
      if (reportId) {
        toast.error(t("dailyReports.list.resendError"), {
          id: `daily-report-resend-${reportId}`,
        });
      }
    },
  });

  if (isError && !isLoading) {
    return (
      <div className="space-y-4 fx-content">
        <PageHeader title={t("dailyReports.detail.title")} />
        <p className="text-muted-foreground">
          {(error as Error)?.message ?? t("dailyReports.detail.loadError")}
        </p>
        <Button asChild variant="outline">
          <Link to="/dgi/daily-reports">{t("dailyReports.detail.backToList")}</Link>
        </Button>
      </div>
    );
  }

  if (!reportId) {
    return (
      <div className="fx-content">
        <p className="text-muted-foreground">{t("dailyReports.detail.notFound")}</p>
        <Button asChild variant="outline" className="mt-4">
          <Link to="/dgi/daily-reports">{t("dailyReports.detail.backToList")}</Link>
        </Button>
      </div>
    );
  }

  if (isLoading || data == null) {
    return <LoadingPage />;
  }

  const sentAt = formatDisplayDate(data.createdAt, { locale: i18n.language });
  const reportDateLabel = formatDisplayDate(
    parseLocalDateOnly(data.fechaResumen),
    { locale: i18n.language }
  );

  return (
    <div className="space-y-4 fx-content">
      <PageHeader
        title={t("dailyReports.detail.title")}
        description={t("dailyReports.detail.subtitle", { date: reportDateLabel })}
        actions={
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <Button asChild variant="outline" className="transition-colors duration-150">
              <Link to="/dgi/daily-reports">{t("dailyReports.detail.backToList")}</Link>
            </Button>
            {canResendDailyReport(data.status) ? (
              <Button
                type="button"
                variant="default"
                className="gap-1.5 transition-colors duration-150"
                disabled={resendMutation.isPending}
                onClick={() => resendMutation.mutate()}
              >
                {resendMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <RefreshCw className="h-4 w-4" aria-hidden />
                )}
                {resendMutation.isPending
                  ? t("dailyReports.list.resending")
                  : t("dailyReports.list.resend")}
              </Button>
            ) : null}
          </div>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-foreground">{t("dailyReports.detail.metaTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          {(() => {
            const ds = dailyReportDisplayStatus(data.status, data.retriesExhausted);
            return (
              <div className="space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-muted-foreground">{t("dailyReports.detail.statusLabel")}</span>
                  <Badge
                    variant={statusBadgeVariant(ds)}
                    className={statusBadgeClassName(ds)}
                  >
                    {t(`dailyReports.status.${ds}`)}
                  </Badge>
                </div>
                {ds === "retrying" ? (
                  <p className="text-sm text-amber-700 dark:text-amber-400">
                    {t("dailyReports.retryingHint")}
                  </p>
                ) : null}
              </div>
            );
          })()}
          <p>
            <span className="text-muted-foreground">{t("dailyReports.detail.dgiEnvioLabel")}</span>{" "}
            <span className="tabular-nums font-medium">{data.secEnvio}</span>
          </p>
          <p>
            <span className="text-muted-foreground">{t("dailyReports.detail.attemptLabel")}</span>{" "}
            <span className="tabular-nums font-medium">{data.attemptNumber}</span>
          </p>
          <p>
            <span className="text-muted-foreground">{t("dailyReports.detail.sentAtLabel")}</span>{" "}
            {sentAt}
          </p>
          {data.errorMessage ? (
            <p className="text-destructive">
              <span className="font-medium">{t("dailyReports.detail.errorLabel")}</span>{" "}
              {data.errorMessage}
            </p>
          ) : null}
        </CardContent>
      </Card>

      {data.summary ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-foreground">{t("dailyReports.detail.summaryTitle")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>
              <span className="text-muted-foreground">{t("dailyReports.detail.totalDocuments")}</span>{" "}
              <span className="font-medium tabular-nums">{data.summary.cantComprobantes}</span>
            </p>
            {data.summary.sections.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("dailyReports.detail.columnCfeType")}</TableHead>
                    <TableHead className="text-right">{t("dailyReports.detail.columnUsed")}</TableHead>
                    <TableHead className="text-right">{t("dailyReports.detail.columnVoided")}</TableHead>
                    <TableHead className="text-right">{t("dailyReports.detail.columnEmitted")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.summary.sections.map((s) => (
                    <TableRow key={`${s.cfeType}-${s.cantDocsUtil}-${s.cantDocsEmi}`}>
                      <TableCell className="font-mono text-sm">{s.cfeType}</TableCell>
                      <TableCell className="text-right tabular-nums">{s.cantDocsUtil}</TableCell>
                      <TableCell className="text-right tabular-nums">{s.cantDocsAnulados}</TableCell>
                      <TableCell className="text-right tabular-nums">{s.cantDocsEmi}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-muted-foreground text-sm">{t("dailyReports.detail.noSections")}</p>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-6">
            <p className="text-muted-foreground text-sm">{t("dailyReports.detail.noSummary")}</p>
          </CardContent>
        </Card>
      )}

      {data.signedXml ? (
        <Collapsible className="rounded-lg border border-border">
          <div className="flex items-stretch gap-2 border-b border-border">
            <CollapsibleTrigger
              className={cn(
                "flex min-w-0 flex-1 items-center justify-between gap-2 px-4 py-3 text-left text-sm font-medium",
                "text-foreground transition-colors hover:bg-muted/80",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              )}
            >
              {t("dailyReports.detail.technicalSentReport")}
              <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
            </CollapsibleTrigger>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="my-2 mr-2 shrink-0 gap-1.5 transition-colors duration-150"
              onClick={(e) => {
                e.preventDefault();
                void handleCopySignedXml();
              }}
            >
              <Copy className="h-4 w-4" aria-hidden />
              {t("dailyReports.detail.copySentReport")}
            </Button>
          </div>
          <CollapsibleContent>
            <pre className="max-h-[min(50vh,420px)] overflow-auto border-t border-border bg-muted/30 p-4 text-xs leading-relaxed">
              {data.signedXml}
            </pre>
          </CollapsibleContent>
        </Collapsible>
      ) : null}

      {data.ackXml ? (
        <Collapsible className="rounded-lg border border-border">
          <div className="flex items-stretch gap-2 border-b border-border">
            <CollapsibleTrigger
              className={cn(
                "flex min-w-0 flex-1 items-center justify-between gap-2 px-4 py-3 text-left text-sm font-medium",
                "text-foreground transition-colors hover:bg-muted/80",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              )}
            >
              {t("dailyReports.detail.technicalAck")}
              <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
            </CollapsibleTrigger>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="my-2 mr-2 shrink-0 gap-1.5 transition-colors duration-150"
              onClick={(e) => {
                e.preventDefault();
                void handleCopyAckXml();
              }}
            >
              <Copy className="h-4 w-4" aria-hidden />
              {t("dailyReports.detail.copyAck")}
            </Button>
          </div>
          <CollapsibleContent>
            <pre className="max-h-[min(50vh,420px)] overflow-auto border-t border-border bg-muted/30 p-4 text-xs leading-relaxed">
              {data.ackXml}
            </pre>
          </CollapsibleContent>
        </Collapsible>
      ) : null}
    </div>
  );
}
